import { useRef, useEffect, useState } from "react";
import { Group, Image as KonvaImage, Transformer } from "react-konva";
import {
  DraggableItem,
  DraggableItemObj,
  RenderElProps,
} from "./draggable-item.class";

import {
  DeleteButton,
  MirrorButton,
  DuplicateElButton,
  MoveUpperButton,
  MoveLowerButton,
  ToggleBoldButton,
  ToggleUnderlineButton,
  ToggleItalicButton,
  FontSizeSelect,
  FontFamilySelect,
  EditTextButton,
} from "../components/draggable-menu-btns";
import { Html } from "react-konva-utils";
import { ItemMenuContainer } from "./comic.class";
import Latex from "react-latex-next";
import html2canvas from "html2canvas";
// T1.6 · o ajuste que faz a letra caber na forma. A medicao que achou o defeito e a que
// descartou o conserto obvio estao no cabecalho de `caber-no-balao.ts`.
import { AREA_UTIL_DO_BALAO, caberNoBalao } from "./caber-no-balao";
import Utils from "./utils.class";
import { abrirEdicaoDeTexto, ehDuploClique } from "./editar-texto-do-balao";
import { EstiloDeBalao, estiloDoItem } from "./balao-do-estilo";

/**
 * AS FONTES QUE O BALAO OFERECE — e esta lista era o "primeiro manual de marca".
 *
 * `T2.96`: *"o canvas ta totalmente com os icones e ESTRUTURAS ANTIGAS DO PRIMEIRO MANUAL DE
 * MARCA que ta aqui fora do manual de marca, na verdade, que ta horroroso."*
 *
 * Medido pela varredura (`rastro/varrer-marca.mjs`) em 2026-09-12: a lista oferecia Arial, Times
 * New Roman, Roboto, Young Serif, Pixelify Sans e Autour One. **Nenhuma das seis esta no manual
 * de marca do HQ-Lab.** E a `especificacao/001-estetica-e-marca.md` e explicita sobre quais sao:
 *
 *   display / titulo        Lilita One
 *   texto / interface / BALAO DE FALA   Fredoka
 *
 * A ultima linha e a que decide isto: *"o T1.6 reclama que o balao usa 'fonte generica sans'. A
 * fonte do balao e FREDOKA. Isso e decisao, nao sugestao."*
 *
 * ── POR QUE A LISTA NAO FICOU COM UMA FONTE SO ─────────────────────────────────────────────
 *
 * Porque o balao de quadrinho usa tipo como GRAMATICA: fala, grito e pensamento nao soam iguais,
 * e o formato de documento v2 ja reconhece os cinco estilos (`Balao.estilo`). Duas familias com
 * papeis diferentes servem a isso; seis familias sem papel nenhum sao seis jeitos de a mesma
 * turma produzir seis paginas que nao parecem do mesmo produto.
 *
 * Entao a lista e a do manual, com o PAPEL de cada uma dito na tela — e nao o nome tecnico da
 * familia, que nao significa nada para quem tem oito anos.
 */
export const fontFamilies = ["Fredoka", "Lilita One"];

/** O que cada uma e PARA, na lingua de quem usa. A tela mostra isto, e nao o nome da familia. */
export const PAPEL_DA_FONTE: Record<string, string> = {
  Fredoka: "Fala (a letra do HQ-Lab)",
  "Lilita One": "Grito ou título (bem grossa)",
};

type TextBubbleObj = Omit<DraggableItemObj, "type"> & {
  text: string;
  fontSize: number;
  fontFamily: string;
  underline: boolean;
  italic: boolean;
  bold: boolean;
  /** T20 · fala, pensamento, grito, sussurro ou narração (recordatório). Ver `estiloDoItem`. */
  estilo?: string;
};
export class TextBubble extends DraggableItem {
  private text: string;
  private fontSize: number;
  private fontFamily: string;
  underline: boolean;
  italic: boolean;
  bold: boolean;
  /**
   * T20 · O ESTILO É GRAVADO. Antes ele não estava aqui nem no `toObj`: o balão desenhava a forma
   * certa ao abrir pela primeira vez e, salvo e reaberto, voltava como fala — "não eram mais
   * recordatórios, eram balões", na letra dele.
   */
  readonly estilo: EstiloDeBalao;

  private setText: React.Dispatch<React.SetStateAction<string>> | undefined;
  private setFontSize: React.Dispatch<React.SetStateAction<number>> | undefined;
  private setFontFamily:
    | React.Dispatch<React.SetStateAction<string>>
    | undefined;
  private setUnderline:
    | React.Dispatch<React.SetStateAction<boolean>>
    | undefined;
  private setItalic: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  private setBold: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  private prevLatexImage: HTMLImageElement | null = null;



  protected menuButtons = [
    DeleteButton,
    EditTextButton,
    MirrorButton,
    DuplicateElButton,
    MoveUpperButton,
    MoveLowerButton,
    ToggleBoldButton,
    ToggleUnderlineButton,
    ToggleItalicButton,
    FontSizeSelect,
    FontFamilySelect,
  ];

  constructor(obj: TextBubbleObj, imgUrl: string) {
    super({ ...obj, type: "textBubbles" }, imgUrl);
    this.text = obj.text;
    this.fontSize = obj.fontSize;
    this.fontFamily = obj.fontFamily;
    this.underline = obj.underline;
    this.italic = obj.italic;
    this.bold = obj.bold;
    this.estilo = estiloDoItem(obj);
  }

  toObj() {
    return {
      ...super.toObj(),
      text: this.text,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      underline: this.underline,
      italic: this.italic,
      bold: this.bold,
      estilo: this.estilo,
    };
  }

  Text(value?: string) {
    if (value !== undefined) {
      this.text = value;
      if (!!this.setText) {
        this.setText(value);
      }
    }
    return this.text;
  }
  FontSize(value?: number) {
    if (value !== undefined) {
      this.fontSize = value;
      if (!!this.setFontSize) {
        this.setFontSize(value);
      }
    }
    return this.fontSize;
  }
  FontFamily(value?: string) {
    if (value !== undefined) {
      this.fontFamily = value;
      if (!!this.setFontFamily) {
        this.setFontFamily(value);
      }
    }
    return this.fontFamily;
  }

  toggleUnderline() {
    this.underline = !this.underline;
    if (!!this.setUnderline) {
      this.setUnderline(this.underline);
    }
  }
  toggleItalic() {
    this.italic = !this.italic;
    if (!!this.setItalic) {
      this.setItalic(this.italic);
    }
  }
  toggleBold() {
    this.bold = !this.bold;
    if (!!this.setBold) {
      this.setBold(this.bold);
    }
  }

  render(props: RenderElProps): JSX.Element {
    const El = () => {
      const shapeRef: React.MutableRefObject<any> = useRef();
      const imgRef: React.MutableRefObject<any> = useRef();
      const trRef: React.MutableRefObject<any> = useRef();
      const itemMenuRef = useRef<HTMLDivElement>(null);

      const latexDivRef = useRef<HTMLDivElement>(null);
      const [latexImage, setLatexImage] = useState<HTMLImageElement | null>(
        this.prevLatexImage
      );

      const [itemMenuEl, setItemMenuEl] = useState<HTMLDivElement | null>(null);
      const [text, setText] = useState(this.text);
      const [fontSize, setFontSize] = useState(this.fontSize);
      const [fontFamily, setFontFamily] = useState(this.fontFamily);
      const [underline, setUnderline] = useState(this.underline);
      const [italic, setItalic] = useState(this.italic);
      const [bold, setBold] = useState(this.bold);

      useEffect(() => {
        this.setText = setText;
        this.setFontSize = setFontSize;
        this.setFontFamily = setFontFamily;
        this.setUnderline = setUnderline;
        this.setItalic = setItalic;
        this.setBold = setBold;
      }, []);

      useEffect(() => {
        this.imageRef = imgRef.current;
        setItemMenuEl(itemMenuRef.current);

        if (props.isSelected) {
          // we need to attach transformer manually
          trRef.current.nodes([shapeRef.current]);
          trRef.current.getLayer().batchDraw();
        }
      }, []);
      // ── A ESCALA SOBE PARA ANTES DO EFEITO, E ISSO E O CONSERTO DO T5.10 ────────────────────
      //
      // Ela era declarada DEPOIS do efeito abaixo, o que a tornava invisivel para a lista de
      // dependencias dele. Ver o comentario do efeito para o defeito que isso causava.
      //
      // (E aqui havia um `console.log(scaleX, scaleY)` disparando a cada render de cada balao. Um
      // log de depuracao esquecido nao e inofensivo: ele enche o console em producao e faz as
      // mensagens que importam — as do `[roteirizar]`, do `[gen]`, do `[saveImg]` — virarem
      // agulha em palheiro justamente quando alguem esta depurando.)
      const scaleX = props.scaleX ?? 1;
      const scaleY = props.scaleY ?? 1;
      // ── A CAIXA TAMBEM SOBE PARA ANTES DO EFEITO, E PELO MESMO MOTIVO DA ESCALA ────────────
      //
      // `this.width` direto na lista de dependencias compila COM AVISO:
      //
      //   React Hook useEffect has a complex expression in the dependency array.
      //   Extract it to a separate variable so it can be statically checked
      //
      // E aviso de compilacao nao e cosmetico nesta bancada: o servidor de desenvolvimento poe um
      // `iframe` em cima da pagina inteira a cada aviso, e ele engole clique (licao 78). Eu descobri
      // isto porque o medidor do `T6.6` parou de achar o cartao de formato depois desta mudanca — o
      // aviso apareceu, o vidro desceu, e o instrumento nao viu mais a tela.
      const larguraDaCaixa = this.width;
      const alturaDaCaixa = this.height;

      useEffect(() => {
        const recursiveFunction = () => {
          if (!latexDivRef.current) {
            Utils.sleep(10).then(() => recursiveFunction());
          } else {
            // ── T1.6 · A LETRA CABE ANTES DE SER RASTERIZADA ──────────────────────────────
            //
            // O ajuste tem de acontecer AQUI, e nao depois: o que vai para o palco e o bitmap, e
            // um bitmap que ja saiu grande nao encolhe sem perder nitidez. Medir, ajustar o corpo
            // no DOM, e so entao rasterizar — tudo na mesma passada, sem o React saber.
            caberNoBalao(
              latexDivRef.current,
              { largura: this.width * scaleX, altura: this.height * scaleY },
              fontSize * scaleX,
            );
            // T20 · o balão pode SAIR DA TELA enquanto o html2canvas trabalha — redimensionar remonta
            // o item. Era daqui o "Cannot read properties of null (reading 'offsetWidth')" e os
            // "Unable to find element in cloned iframe". Elemento fora do documento não se
            // rasteriza; e o que já saiu não precisa de bitmap.
            const div = latexDivRef.current;
            if (!div.isConnected) return;
            html2canvas(div, { backgroundColor: null }).then(
              (canvas) => {
                if (!div.isConnected) return;
                const dataUrl = canvas.toDataURL("image/png");
                const image: HTMLImageElement = new Image(
                  div.offsetWidth,
                  div.offsetHeight
                );
                image.src = dataUrl;
                image.onload = () => {
                  this.prevLatexImage = image;
                  setLatexImage(image);
                };
              }
            ).catch(() => {
              // O balão saiu da tela no meio do caminho: nada a desenhar, e nada a derrubar.
            });
          }
        };
        recursiveFunction();
        // ── `scaleX` E `scaleY` ENTRARAM AQUI, E E O CONSERTO DO T5.10 ────────────────────────
        //
        // Ele, na letra: *"nao ta mantendo a formatacao (...) Nao mantem a formatacao interna
        // dentro do negocio, no meu quadro (...) ta meio bugado (...) **a preview nao significa
        // que e o formato correto**."*
        //
        // O texto de um balao nao e desenhado pelo Konva: ele e RASTERIZADO por `html2canvas` a
        // partir do `<div>` acima, e o `<div>` usa `fontSize: fontSize * scaleX`. Entao o bitmap
        // do texto depende da escala.
        //
        // E a lista de dependencias nao tinha a escala. Consequencia: quando a escala muda — e ela
        // muda entre o EDITOR e a PREVIA, que dimensionam o palco de formas diferentes —, a caixa
        // do balao reescala e o TEXTO DENTRO DELE FICA COM O BITMAP DA ESCALA ANTIGA. Balao de um
        // tamanho, letra de outro. "A preview nao significa que e o formato correto", na letra.
        //
        // E era pior do que uma dependencia faltando, porque a escala era declarada ABAIXO deste
        // efeito: o efeito fechava sobre o valor do PRIMEIRO render, e `this.prevLatexImage`
        // guardava esse bitmap entre remontagens.
        //
        // Nao ha risco de laco: o efeito escreve `latexImage`, que nao afeta a escala. E nao ha
        // custo no zoom, porque o zoom da folha vai no `<Konva.Layer scaleX=...>` e nao neste
        // `scaleX` — este muda so quando a caixa que contem o palco muda de tamanho.
        //
        // ── E A CAIXA ENTROU NA LISTA EM 28/09, E SEM ELA O `T5.10` NAO ANDA ─────────────────
        //
        // `caberNoBalao` recebe `this.width` e `this.height` seis linhas acima, e eles NAO estavam
        // nas dependencias: arrastar a alca mudava a caixa e este efeito nao rodava de novo.
        // Medido: a caixa foi de 146x146 a 298x298 e o bitmap do texto ficou em 98x19.
        //
        // Isto e METADE do conserto, e a tentativa de 27/09 fez so esta metade — e nao mudou nada,
        // porque `ajusteParaCaber` devolvia `fator: 1` sempre que o texto ja cabia. A outra metade
        // esta la, e e a lei da letra: ela CRESCE com a caixa, com o custo de errar escrito.
      }, [text, bold, underline, italic, fontSize, fontFamily, scaleX, scaleY, larguraDaCaixa, alturaDaCaixa]);

      /*
      let fontStyle = "";
      if (italic) fontStyle += "italic ";
      if (bold) fontStyle += "bold ";
      */

      return (
        <>
          <Group
            ref={shapeRef}
            draggable={props.isSelected}
            x={this.x * scaleX}
            y={this.y * scaleY}
            width={this.width * scaleX}
            height={this.height * scaleY}
            rotation={this.rotation}
            // O clique seleciona, como sempre — e além disso conta o intervalo: dois cliques
            // dentro de 400 ms abrem a caixa de escrever. A contagem vem antes do `props.onClick`
            // porque `onClick` dispara o redesenho que troca este nó.
            onClick={() => {
              // Pelo `id`, e nao por esta instancia: `layer.copy()` clona todo item a cada
              // redesenho, e o redesenho acontece no proprio clique. O `id` sobrevive ao clone.
              const duplo = ehDuploClique(this.id);
              props.onClick();
              if (duplo) abrirEdicaoDeTexto(this, props.setPopUpInfo);
            }}
            // ── O CLIQUE DUPLO ESCREVE — `T8.10` ──────────────────────────────────────────────
            //
            // Ele, na letra: *"selecioná-lo com clique duplo não abriu nenhuma edição de texto"*.
            // Agora abre, e abre A MESMA caixa do botão de lápis do menu — uma função só, em
            // `editar-texto-do-balao.ts`.
            //
            // `onDblTap` junto porque no toque o gesto é outro evento com o mesmo significado, e
            // este produto é usado em tablet de escola. Sem ele, o conserto existiria só para quem
            // tem mouse.
            //
            // OS DOIS FICAM, mesmo com a contagem própria acima: quando o nó NÃO é substituído — o
            // balão já selecionado, clicado duas vezes seguidas — é o Konva que sintetiza, e é de
            // graça. Se os dois dispararem, a caixa é aberta duas vezes com o mesmo conteúdo, o que
            // na tela é uma caixa só.
            onDblClick={() => abrirEdicaoDeTexto(this, props.setPopUpInfo)}
            onDblTap={() => abrirEdicaoDeTexto(this, props.setPopUpInfo)}
            onDragMove={(e) => props.onDragMove(e, itemMenuRef.current)}
            onDragEnd={props.onDragEnd}
            onTouchStart={(e) => this.onTouchStart(e)}
            onTouchEnd={(e) => this.onTouchEnd(e, props.onClick)}
            onTouchMove={(e) => {
              this.onTouchMove(e, !props.isSelected);
            }}
          >
            <KonvaImage
              ref={imgRef}
              image={this.getImage()}
              width={this.width * scaleX}
              height={this.height * scaleY}
            />
            <Html divProps={{ style: { zIndex: -100 } }}>
              <div
                style={{
                  // background: "gray",
                  background: "transparent",
                  display: "flex",
                  justifyContent: "center",
                  position: "absolute",
                  width: this.width * scaleX,
                  top: (this.height * scaleY) / 2,
                  // left: -window.innerWidth * 1e6,
                  transform: `rotate(${-this.rotation}deg)`,
                  // whiteSpace: "nowrap", // Evita que quebre linhas
                  pointerEvents: "none", // Impede interferência no clique/drag do Konva
                }}
              >
                <span
                  ref={latexDivRef}
                  style={{
                    // transform: `rotate(${-this.rotation}deg)`,
                    textAlign: "center",
                    fontWeight: bold ? "bold" : "normal",
                    fontStyle: italic ? "italic" : "normal",
                    textDecoration: underline ? "underline" : "none",
                    fontSize: fontSize * scaleX + "px",
                    fontFamily: fontFamily,
                    // T1.6 · O TETO DE LARGURA E O QUE FAZ A MEDIDA SER DA LETRA, E NAO DA CAIXA.
                    //
                    // Sem ele o span esticava ate os 150 px do contentor e a medicao devolvia 150
                    // em qualquer corpo de fonte — medido: com o ajuste ligado e sem este teto, a
                    // altura caiu de 100 para 33 e a largura ficou cravada em 150, ou seja, a letra
                    // continuava tocando a curva do balao.
                    //
                    // Com o teto, o texto quebra na area util, a altura cresce, e e a altura que o
                    // `caberNoBalao` usa para encolher o corpo. As duas coisas trabalham juntas: o
                    // teto decide ONDE quebra, e o ajuste decide QUANTO cabe.
                    maxWidth: this.width * scaleX * AREA_UTIL_DO_BALAO.largura + "px",
                  }}
                >
                  <Latex>{text}</Latex>
                </span>
              </div>
            </Html>
            {latexImage && (
              <KonvaImage
                image={latexImage}
                x={(this.width * scaleX) / 2 - latexImage.width / 2}
                y={(this.height * scaleY) / 2 - latexImage.height / 2}
              />
            )}
          </Group>

          {props.isSelected && (
            <>
              {/* `transform={false}` — E ISSO CONSERTA UM DEFEITO MEDIDO.

                  O `<Html>` do react-konva aplica a transformacao do PALCO no `<div>` que ele
                  cria, e o palco carrega o ZOOM da folha. Medido em 13/09, com a folha a 91%: os
                  botoes do menu do item, declarados com 44px, chegavam a tela com 40 — 44 x 0,91.
                  A 24% (uma A4 numa janela baixa) seriam 10px.

                  Alvo de toque que encolhe conforme o zoom da folha nao e alvo de toque. E o menu
                  ja se posiciona em COORDENADAS DE TELA (`getItemMenuPos`) num container
                  `position: fixed` — a transformacao do palco por cima disso era escala a mais,
                  nao posicionamento. */}
              <Html
                transform={false}
                // O `<Html>` cria um `<div>` DENTRO do container do palco. Com a transformacao
                // ligada ele o deixa `position: absolute`; com `transform={false}` ele LIMPA o
                // estilo, e o div cai no fluxo normal — ocupando espaco e COBRINDO parte do
                // canvas. Medido: depois de trocar para `transform={false}`, os quadros 2 e 3 da
                // tirinha pararam de selecionar, porque o div comia o clique.
                //
                // Zerado e absoluto, ele volta a nao ocupar nada. O menu em si e `position:
                // fixed` e se posiciona sozinho, entao nao precisa de caixa nenhuma.
                divProps={{
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                  },
                }}
              >
                <ItemMenuContainer
                  ref={itemMenuRef}
                  style={this.getItemMenuPos(itemMenuEl)}
                >
                  {this.getMenuBts(props)}
                </ItemMenuContainer>
              </Html>

              <Transformer
                ref={trRef}
                boundBoxFunc={(oldBox, newBox) =>
                  props.boundBoxFunc(oldBox, newBox, itemMenuRef.current)
                }
                onTransformEnd={props.onTransformEnd}
              />
            </>
          )}
        </>
      );
    };
    return <El />;
  }
}
